import { useState } from "react";
import { api, gbp, getCurrency } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Luggage, Armchair, Zap } from "lucide-react";

const qtyLabel = { hotel: "Nights", carhire: "Days" };
const paxTypes = ["flight", "holiday", "umrah", "hajj"];
const depositTypes = ["hajj"];

const ADDONS = [
  { key: "bag", name: "Extra checked bag (23kg)", price: 45, icon: Luggage },
  { key: "seat", name: "Extra legroom seat", price: 25, icon: Armchair },
  { key: "priority", name: "Priority boarding", price: 12, icon: Zap },
];

export const BookingDialog = ({ productType, item, trigger, unitPrice, defaultPax }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    lead_name: "", email: "", phone: "", notes: "",
    adults: defaultPax?.adults || 1, children: defaultPax?.children || 0, infants: defaultPax?.infants || 0,
    quantity: 1,
  });
  const [extraPax, setExtraPax] = useState([]);
  const [passport, setPassport] = useState({});   // index -> {passport, dob, nationality}
  const [showPassport, setShowPassport] = useState(false);
  const [addonQty, setAddonQty] = useState({});    // key -> qty
  const [depositMode, setDepositMode] = useState(true);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPax = (i, v) => setExtraPax((p) => { const n = [...p]; n[i] = v; return n; });
  const setPp = (i, k, v) => setPassport((p) => ({ ...p, [i]: { ...(p[i] || {}), [k]: v } }));

  const showPax = paxTypes.includes(productType);
  const showQty = productType === "hotel" || productType === "carhire";
  const showDeposit = depositTypes.includes(productType) && Number(item.deposit) > 0;
  const showAddons = productType === "flight";

  const adults = Number(form.adults) || 1;
  const children = Number(form.children) || 0;
  const infants = Number(form.infants) || 0;
  const totalPax = Math.max(1, adults + children + infants);
  const extraCount = showPax ? Math.max(0, totalPax - 1) : 0;

  const addonsTotal = showAddons ? ADDONS.reduce((s, a) => s + a.price * (addonQty[a.key] || 0), 0) : 0;
  const fullTotal = (productType === "flight"
    ? unitPrice * adults + unitPrice * 0.7 * children + unitPrice * 0.1 * infants
    : unitPrice * (Number(form.adults) || 1) + unitPrice * 0.7 * children) + addonsTotal;
  const depositTotal = Number(item.deposit || 0) * Math.max(1, adults + children);
  const payNow = showDeposit && depositMode ? depositTotal + addonsTotal : fullTotal;

  const typeFor = (idx) => idx < adults ? "Adult" : idx < adults + children ? "Child" : "Infant";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const names = showPax
        ? [`${form.lead_name} (${typeFor(0)})`, ...Array.from({ length: extraCount }).map((_, i) => {
            const n = (extraPax[i] || "").trim();
            return n ? `${n} (${typeFor(i + 1)})` : `${typeFor(i + 1)} ${i + 2}`;
          })]
        : [form.lead_name];
      const passenger_details = showPax ? names.map((nm, i) => ({
        name: nm, type: typeFor(i),
        passport: passport[i]?.passport || "", dob: passport[i]?.dob || "", nationality: passport[i]?.nationality || "",
      })) : [];
      const addons = ADDONS.filter((a) => (addonQty[a.key] || 0) > 0)
        .map((a) => ({ name: a.name, price: a.price, qty: addonQty[a.key] }));

      const { data: booking } = await api.post("/bookings", {
        product_type: productType,
        item_id: item.id,
        adults, children, infants,
        quantity: Number(form.quantity) || 1,
        lead_name: form.lead_name,
        email: form.email,
        phone: form.phone,
        notes: form.notes,
        deposit: showDeposit ? depositMode : false,
        passengers: names,
        passenger_details,
        addons,
      });
      const { data: session } = await api.post("/payments/checkout", {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      window.location.href = session.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start booking. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Complete your booking</DialogTitle>
          <DialogDescription>Enter your details to proceed to secure payment.</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-slate-500 -mt-2">{item.title || item.name || `${item.airline || ""} flight`}</p>
        <form onSubmit={submit} className="space-y-3 mt-1">
          <div>
            <Label>Lead passenger name</Label>
            <Input data-testid="booking-name" required value={form.lead_name} onChange={(e) => set("lead_name", e.target.value)} placeholder="John Smith" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Email</Label><Input data-testid="booking-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
            <div><Label>Phone</Label><Input data-testid="booking-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
          </div>
          {showPax && (
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Adults</Label><Input data-testid="booking-adults" type="number" min="1" max="20" value={form.adults} onChange={(e) => set("adults", e.target.value)} /></div>
              <div><Label>Children</Label><Input data-testid="booking-children" type="number" min="0" value={form.children} onChange={(e) => set("children", e.target.value)} /></div>
              <div><Label>Infants</Label><Input data-testid="booking-infants" type="number" min="0" value={form.infants} onChange={(e) => set("infants", e.target.value)} /></div>
            </div>
          )}
          {showPax && extraCount > 0 && (
            <div className="space-y-2">
              <Label className="text-xs text-slate-500">Other travellers (full name as on passport)</Label>
              {Array.from({ length: extraCount }).map((_, i) => (
                <Input key={i} data-testid={`booking-pax-${i + 2}`} value={extraPax[i] || ""} placeholder={`${typeFor(i + 1)} ${i + 2} full name`} onChange={(e) => setPax(i, e.target.value)} />
              ))}
            </div>
          )}
          {showQty && (
            <div><Label>{qtyLabel[productType]}</Label><Input data-testid="booking-quantity" type="number" min="1" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} /></div>
          )}

          {showAddons && (
            <div className="rounded-xl border border-slate-200 p-3 space-y-2">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Add extras (optional)</p>
              {ADDONS.map((a) => {
                const Icon = a.icon; const q = addonQty[a.key] || 0;
                return (
                  <div key={a.key} className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-sm text-slate-700"><Icon className="w-4 h-4 text-[#328CC1]" /> {a.name} <span className="text-slate-400">· {gbp(a.price)}</span></span>
                    <div className="flex items-center gap-2">
                      <button type="button" data-testid={`addon-${a.key}-minus`} onClick={() => setAddonQty((s) => ({ ...s, [a.key]: Math.max(0, q - 1) }))} className="w-6 h-6 rounded-full border border-slate-300 leading-none">−</button>
                      <span data-testid={`addon-${a.key}-qty`} className="w-4 text-center text-sm font-semibold">{q}</span>
                      <button type="button" data-testid={`addon-${a.key}-plus`} onClick={() => setAddonQty((s) => ({ ...s, [a.key]: q + 1 }))} className="w-6 h-6 rounded-full border border-slate-300 leading-none">+</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {showPax && (
            <div>
              <button type="button" data-testid="booking-passport-toggle" onClick={() => setShowPassport((s) => !s)} className="text-sm text-[#0B3C5D] font-semibold">
                {showPassport ? "− Hide" : "+ Add"} passport details (optional)
              </button>
              {showPassport && (
                <div className="space-y-3 mt-2">
                  {Array.from({ length: totalPax }).map((_, i) => (
                    <div key={i} className="rounded-lg bg-slate-50 border border-slate-200 p-2 space-y-2">
                      <p className="text-[11px] font-semibold text-slate-500">{typeFor(i)} {i + 1}{i === 0 ? " (lead)" : ""}</p>
                      <div className="grid grid-cols-3 gap-2">
                        <Input data-testid={`booking-passport-${i}`} placeholder="Passport no." value={passport[i]?.passport || ""} onChange={(e) => setPp(i, "passport", e.target.value)} />
                        <Input data-testid={`booking-dob-${i}`} type="date" value={passport[i]?.dob || ""} onChange={(e) => setPp(i, "dob", e.target.value)} />
                        <Input data-testid={`booking-nat-${i}`} placeholder="Nationality" value={passport[i]?.nationality || ""} onChange={(e) => setPp(i, "nationality", e.target.value)} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {showDeposit && (
            <div className="grid grid-cols-2 gap-2">
              <button type="button" data-testid="booking-deposit-option" onClick={() => setDepositMode(true)}
                className={`rounded-xl border p-3 text-left transition-colors ${depositMode ? "border-[#FF6B35] bg-[#FF6B35]/5" : "border-slate-200 hover:border-slate-300"}`}>
                <span className="block text-[11px] text-slate-400">Deposit now</span>
                <span className="block text-lg font-display font-bold text-[#0B3C5D]">{gbp(depositTotal + addonsTotal)}</span>
                <span className="block text-[10.5px] text-slate-400">Balance {gbp(Math.max(0, fullTotal - depositTotal - addonsTotal))} later</span>
              </button>
              <button type="button" data-testid="booking-full-option" onClick={() => setDepositMode(false)}
                className={`rounded-xl border p-3 text-left transition-colors ${!depositMode ? "border-[#FF6B35] bg-[#FF6B35]/5" : "border-slate-200 hover:border-slate-300"}`}>
                <span className="block text-[11px] text-slate-400">Pay in full</span>
                <span className="block text-lg font-display font-bold text-[#0B3C5D]">{gbp(fullTotal)}</span>
                <span className="block text-[10.5px] text-slate-400">No balance due</span>
              </button>
            </div>
          )}

          <div className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3 border border-slate-200">
            <span className="text-sm text-slate-500">{showDeposit && depositMode ? "Pay now (deposit)" : "Total"}</span>
            <span data-testid="booking-total" className="text-2xl font-display font-bold text-[#0B3C5D]">{gbp(payNow)}</span>
          </div>
          <Button data-testid="booking-pay-btn" type="submit" disabled={loading}
            className="w-full rounded-full bg-[#FF6B35] hover:bg-[#e85a26] text-white font-semibold py-6">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Proceed to Secure Payment"}
          </Button>
          <p className="text-[11px] text-slate-400 flex items-center justify-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Secured by Stripe · Test card 4242 4242 4242 4242</p>
          {getCurrency() !== "GBP" && <p className="text-[11px] text-center text-slate-400">Prices shown in {getCurrency()} for reference · you'll be charged in GBP</p>}
        </form>
      </DialogContent>
    </Dialog>
  );
};
